import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Spinner, Clock, X, Buildings } from "@phosphor-icons/react";

/**
 * Collect a browser fingerprint that identifies the PC reasonably well
 * without requiring any plugins or native installation. Combined with the
 * device_token cookie, it gives admins enough context to approve.
 */
function collectFingerprint() {
  let gpu = "";
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (gl) {
      const dbg = gl.getExtension("WEBGL_debug_renderer_info");
      if (dbg) {
        gpu = `${gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) || ""} ${gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || ""}`.trim();
      }
    }
  } catch (e) { /* ignore */ }
  return {
    platform: navigator.platform || "",
    language: navigator.language || "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    screen: `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio || 1}`,
    gpu,
    cpuCores: navigator.hardwareConcurrency || 0,
    deviceMemory: navigator.deviceMemory || 0,
  };
}

/**
 * DeviceGate — wraps the app after login. On mount:
 *  1. Asks backend for device status
 *  2. If 'none' → posts fingerprint → receives HttpOnly cookie, shows "pending" screen
 *  3. If 'pending' → shows pending screen with polling
 *  4. If 'approved' → renders children (the CRM)
 *  5. If 'revoked' → shows blocked screen
 *
 * Admin users bypass this gate entirely (enforced both client and server side).
 */
export default function DeviceGate({ user, children }) {
  const [status, setStatus] = useState("loading"); // loading | none | pending | approved | revoked
  const [deviceName, setDeviceName] = useState("");
  const registering = useRef(false);

  const check = async () => {
    try {
      const { data } = await api.get("/devices/my-status");
      setStatus(data.status || "none");
      setDeviceName(data.device_name || "");
      if (data.status === "none") {
        if (registering.current) return; // guard against double call in StrictMode
        registering.current = true;
        try {
          await api.post("/devices/register", { fingerprint: collectFingerprint() });
          const { data: d2 } = await api.get("/devices/my-status");
          setStatus(d2.status || "pending");
        } finally { registering.current = false; }
      }
    } catch (err) {
      console.error("[DeviceGate]", err);
      setStatus("error");
    }
  };

  useEffect(() => {
    // Admins and marketing-demo accounts bypass the device gate entirely so
    // screen-recording bots / sales demos never get stuck on the pending screen.
    if (user?.role === "admin" || user?.is_demo_account === true) {
      setStatus("approved");
      return;
    }
    check();
    // Poll every 15s if still pending so users see approval without reloading
    const id = setInterval(() => {
      setStatus(s => {
        if (s === "pending") check();
        return s;
      });
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  if (user?.role === "admin" || user?.is_demo_account === true || status === "approved") return children;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="device-gate">
      <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 text-center space-y-5 shadow-2xl">
        {(status === "loading" || status === "none") && (
          <>
            <Spinner size={48} className="animate-spin text-primary mx-auto" />
            <div>
              <h2 className="text-xl font-bold font-[Manrope]">Verificando dispositivo...</h2>
              <p className="text-sm text-muted-foreground mt-2">Registrando esta PC de forma segura.</p>
            </div>
          </>
        )}
        {status === "pending" && (
          <>
            <div className="w-20 h-20 rounded-full bg-amber-500/15 flex items-center justify-center mx-auto">
              <Clock size={40} weight="duotone" className="text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold font-[Manrope]">Dispositivo pendiente de aprobacion</h2>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Esta PC aun no esta autorizada para usar Commandix. Por seguridad, solo el administrador puede aprobar nuevos dispositivos.
              </p>
            </div>
            <div className="bg-secondary/50 border border-border rounded-lg p-4 text-left space-y-2 text-xs">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Buildings size={14} /> Contacta a tu administrador
              </div>
              <p className="text-foreground">
                Indicale que "<strong>{user?.name}</strong>" intenta acceder desde una PC nueva. El vera la solicitud en su panel y te aprobara.
              </p>
            </div>
            <p className="text-[10px] text-muted-foreground">Esta pagina se actualizara sola cuando seas aprobado.</p>
            <button onClick={check} className="text-xs text-primary hover:underline">Verificar ahora</button>
          </>
        )}
        {status === "revoked" && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/15 flex items-center justify-center mx-auto">
              <X size={40} weight="bold" className="text-red-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold font-[Manrope]">Acceso revocado</h2>
              <p className="text-sm text-muted-foreground mt-2">
                Esta PC ({deviceName || "sin nombre"}) fue revocada por el administrador. Contactalo para restaurar acceso.
              </p>
            </div>
          </>
        )}
        {status === "error" && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/15 flex items-center justify-center mx-auto">
              <X size={40} weight="bold" className="text-red-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold font-[Manrope]">Error de verificacion</h2>
              <p className="text-sm text-muted-foreground mt-2">No se pudo verificar tu dispositivo. Reintenta en unos segundos.</p>
            </div>
            <button onClick={check} className="text-sm text-primary hover:underline">Reintentar</button>
          </>
        )}
      </div>
    </div>
  );
}

export { collectFingerprint };
